import * as React from "react";
import { useReferenceContextProvider } from "../../../Core/ReferenceContextProvider";
import { PlayStateEnum, WaveformType } from "../interfaces/state";
import { useWebPortalApiClient } from "../../../misc/api/WebPortalTokenClient";
import { useCoughMarshalApiClient } from "../../../misc/api/CoughMarshalApiClient";
import * as Actions from "../actions";
import { connect } from 'react-redux';
import { Dispatch } from "redux";
import { AnalyseState } from '../interfaces/state';
import { PeaksPoint } from '../interfacesPeaks/PeaksJS';
import { CoughAnalysisSet, StudyCoughEventType } from 'webportalclient';
import { CoughAnalysisSetComment, CoughEventTypeEnum, OverreadStatusEnum, OverreadStatusIdEnum } from '../../../misc/api/apiTypes';

interface SelectTimeFrameSegmentProps {
    primaryAnalysisSetId: string;
    sessionId: string;
    waveformSelected: string;
    selectedSegment: number;
    isPlaying: PlayStateEnum;
    dispatch?: Dispatch<AnalyseState>;
    pointEvents: PeaksPoint[];
    availableEventTypes: StudyCoughEventType[];
    rawTimepoints: Map<string, number>;
    eventDuplicateThreshold: number;
    onChange: (value) => string;
    isViewOnly: boolean;
}

export const SelectTimeFrameSegment = ({ primaryAnalysisSetId, sessionId, waveformSelected, dispatch, onChange, availableEventTypes, selectedSegment, isPlaying, pointEvents, rawTimepoints, eventDuplicateThreshold, isViewOnly }: SelectTimeFrameSegmentProps) => {
    const [waveformDurations, setWaveformDurations] = React.useState([]);
    const coughMarshalClient = useCoughMarshalApiClient();

    const checkEventDuplicates = () => {

        if (isViewOnly) {
            return false;
        }

        dispatch(Actions.SetAcceptanceErrorAction.create(null));

        for (const point of pointEvents) {
            point.withinDuplicateThreshold = false;
        }

        const coughPeakEvent = availableEventTypes.find(x => x.refEventTypeId === CoughEventTypeEnum.CoughPeak);

        const valuesInThreshold = pointEvents.filter(x => x.layerPosition === 0 && x.eventTypeId === coughPeakEvent.id)
            .filter((ev1, _, entries) =>
                entries.some((ev2) => ev1.id !== ev2.id && Math.abs((rawTimepoints.get(ev1.id) * 1000) - (rawTimepoints.get(ev2.id) * 1000)) <= eventDuplicateThreshold)
            );

        const remainingUnconfirmed = valuesInThreshold.filter(x => !x.confirmedValidDuplicate);

        if (remainingUnconfirmed.length > 0) {
            for (const point of remainingUnconfirmed) {
                point.withinDuplicateThreshold = true;
            }
            window.scrollTo(0, 0);
            const thresholdInSeconds = eventDuplicateThreshold / 1000;
            dispatch(Actions.SetAcceptanceErrorAction.create(`${remainingUnconfirmed.length} events are within the duplicate threshold of ${thresholdInSeconds}s please review before continuing`));

            return true;
        }
        else {
            return false;
        }


    }

    React.useEffect(() => {
        const getWaveformDurations = async () => {
            const recording = await coughMarshalClient.getRecording(sessionId, "splits");

            const segments = new Set(recording.splits.map(split => split.index)).size;

            const durationLabels = Array.from({ length: segments }, (_, index) => {
                const start = index * 24;
                const end = (index + 1) * 24;
                return `${start}-${end}`;
            });

            setWaveformDurations(durationLabels);
            if (segments > 1) {
                dispatch(
                    Actions.IsSegmentedAction.create(
                        true
                    ))
            }
            else {
                dispatch(
                    Actions.IsSegmentedAction.create(
                        false
                    ))
            }

        }

        getWaveformDurations();
    }, [waveformSelected]);

    if (waveformDurations && waveformDurations.length > 1) {

        const initialSelectedSegment = selectedSegment ?? 1;

        return (
            <select
                id="ToggleWaveformDuration"
                className="btn btn-primary"
                value={initialSelectedSegment}
                disabled={isPlaying === PlayStateEnum.Playing}
                onChange={e => {
                    const duplicateEvents = checkEventDuplicates();
                    if (!duplicateEvents) {
                        e.target.blur();
                        onChange(e.target.selectedIndex + 1);
                    }
                }}>
                {waveformDurations.map((x, index) =>
                    <option key={x} value={index + 1}>
                        {x}
                    </option>
                )}
            </select>
        );
    } else {
        return (
            <select
                id="ToggleWaveformDuration"
                className="btn btn-primary"
                value={selectedSegment}
                disabled={true}
                onChange={e => {
                    e.target.blur();
                    onChange(e.target.value);
                }}>
                <option key="0-24">0-24</option>
            </select>
        );
    }
}
